import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Button,
  Rate,
  Typography,
  Popconfirm,
  Space,
  message,
  Select,
  Col,
  Row,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MessageOutlined,
  UserOutlined,
  StarOutlined,
  ShoppingOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  Modal,
  Avatar,
  Tabs,
  Divider,
  Spin,
} from "antd";
import dayjs from "dayjs";
import AddProductFeedback from "../../component/AddProductFeedback";
import ProductFeedbackServ from "../../service/ProductFeedbackServ";
import ShopServ from "../../service/ShopServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import EmptyState from "../../util/EmptyState";
import { FilterBar, SecondaryFilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import FormSelect from "../../component/ui/FormSelect";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import DataTable from "../../component/ui/DataTable";
import TableImage from "../../component/ui/TableImage";
import { getImageUrl as getNormalizedImageUrl } from "../../component/SafeImage";
import { useAdminTable } from "../hooks/useAdminTable";

const { Title } = Typography;

const ProductFeedback = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [addProductFeedback, setAddProductFeedback] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [productFeedbackDetails, setProductFeedbackDetails] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [modalLoading, setModalLoading] = useState(false);

  // Fetch categories from API
  // ✅ Fetch Data
  const fetchProductFeedback = async (params) => {
    const filterObj = {};
    if (params.category) filterObj["product.categoryId"] = params.category;
    if (params.rating) filterObj.rating = params.rating;
    if (params.dateRange && params.dateRange[0] && params.dateRange[1]) {
      filterObj.createdAt = {
        $gte: params.dateRange[0].startOf('day').toISOString(),
        $lte: params.dateRange[1].endOf('day').toISOString(),
      };
    }
    if (params.search) {
      filterObj["$or"] = [
        { "product.name": { $regex: params.search, $options: "i" } },
        { "user.name": { $regex: params.search, $options: "i" } },
        { "comments": { $regex: params.search, $options: "i" } }
      ];
    }

    const apiParams = {
      limit: params.limit,
      skip: (params.page - 1) * params.limit,
      filter: JSON.stringify(filterObj),
    };
    const response = await ProductFeedbackServ.index(apiParams);
    return { data: response?.data || [], total: response?.total || 0 };
  };

  const {
    data: productFeedback,
    setData: setProductFeedback,
    total: totalProductFeedback,
    loading,
    params,
    setParams,
    handleSearch,
    handleTableChange,
    refresh
  } = useAdminTable(fetchProductFeedback, "admin_product_feedback", { page: 1, limit: 10, category: null, rating: null, dateRange: null });

  const searchText = params.search;
  const dateRange = params.dateRange;
  const selectedCategory = params.category;
  const selectedRating = params.rating;

  const fetchCategories = async () => {
    try {
      const CategoryServ = require("../../service/CategoryServ").default;
      const res = await CategoryServ.index({ limit: "all" });
      // Only show top-level (parent) categories
      const allCats = res.data || [];
      const parentOnly = allCats.filter(cat => !cat.parentId || cat.parentId === null || cat.parentId === "");
      setCategories(parentOnly);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  // ✅ Add
  const handleAddProductFeedback = () => {
    setProductFeedbackDetails(null);
    setIsEditing(false);
    setAddProductFeedback(true);
  };

  // ✅ Edit
  const handleEditProductFeedback = (record) => {
    setProductFeedbackDetails(record);
    setIsEditing(true);
    setAddProductFeedback(true);
  };

  // ✅ Delete
  const handleDeleteProductFeedback = async (record) => {
    try {
      const idToDelete = record._id || record.id;
      await ProductFeedbackServ.remove(idToDelete);
      message.success("Feedback removed successfully");
      refresh();
    } catch (error) {
      message.error("Failed to remove feedback");
    }
  };

  // ✅ Refresh (after save)
  const refreshProductFeedback = async (isRefresh) => {
    setAddProductFeedback(false);
    if (isRefresh) refresh();
  };

  const handleReset = () => {
    setParams({ page: 1, limit: 10, search: "", category: null, rating: null, dateRange: null });
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "product",
      key: "productImage",
      width: 70,
      align: "center",
      render: (product) => <TableImage src={getNormalizedImageUrl(product?.imageUrl)} alt="product" size={50} />,
    },
    {
      title: "Product Name",
      dataIndex: "product",
      key: "product",
      render: (product) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>{product?.name || "-"}</span>
        </div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar
            size="small"
            src={record.user?.imageUrl ? getNormalizedImageUrl(record.user?.imageUrl) : null}
            icon={<UserOutlined style={{ color: '#64748b' }} />}
            style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}
          />
          <span style={{ fontWeight: 600, color: '#475569', fontSize: '12px' }}>
            {record.user ? `${record.user.firstName} ${record.user.lastName}` : record.userName || "-"}
          </span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (
        <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 500 }}>
          {new Date(date).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={async (e) => {
            e.stopPropagation();
            setDetailsVisible(true);
            setSelectedRecord(record);
            setModalLoading(true);
            try {
              const fullRes = await ProductFeedbackServ.single(record._id || record.id);
              const fullData = fullRes?.data || fullRes;
              setSelectedRecord(fullData);
            } catch (err) {
              console.error("Error fetching full feedback details:", err);
            } finally {
              setModalLoading(false);
            }
          }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const handleTabChange = (key) => {
    setActiveTab(key);
    setParams(prev => ({ 
      ...prev, 
      rating: key === "All" ? null : parseInt(key), 
      page: 1 
    }));
  };

  const statusTabs = [
    { key: "All", label: "All Feedback" },
    { key: "5", label: "5 Stars" },
    { key: "4", label: "4 Stars" },
    { key: "3", label: "3 Stars" },
    { key: "2", label: "2 Stars" },
    { key: "1", label: "1 Star" },
  ];

  const topRef = React.useRef(null);

  return (
    <>
      <div className="pcu-padding-24" ref={topRef}>
        <div className="pcu-margin-bottom-16">
          <Breadcrumbs />
        </div>

        <div className="sd-container" style={{ padding: '24px' }}>
          {/* Premium Header */}
          <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <MessageOutlined style={{ fontSize: '22px', color: '#4776E6' }} />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Product Feedback</h1>
                <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>{totalProductFeedback} feedback entries</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button
                type="primary"
                onClick={handleAddProductFeedback}
                style={{
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)',
                  border: 'none',
                  padding: '0 20px',
                  fontSize: '13px',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(71, 118, 230, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <PlusOutlined style={{ fontSize: '16px' }} />
                Add Feedback
              </Button>
            </div>
          </div>

          {/* Status Tabs */}
          <div style={{ marginBottom: '24px', borderBottom: '1px solid #f1f5f9' }}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={statusTabs}
              className="premium-tabs-minimal"
              style={{ marginBottom: -1 }}
            />
          </div>

          <div className="pcu-padding-0">
            {/* Filter Bar Grid */}
            <div className="ss-filter-bar" style={{ background: 'transparent', border: 'none', padding: '0 0 16px 0', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', alignItems: 'flex-end' }}>
              <div className="ss-filter-field">
                <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>SEARCH FEEDBACK</span>
                <SearchInput
                  value={searchText}
                  onChange={(v) => handleSearch(v)}
                  placeholder="Search by product, user, or comments..."
                  style={{ height: '38px', borderRadius: '10px' }}
                />
              </div>

              <div className="ss-filter-field">
                <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>FILTER BY CATEGORY</span>
                <FormSelect
                  placeholder="All Categories"
                  value={selectedCategory}
                  onChange={(v) => setParams(prev => ({ ...prev, category: v, page: 1 }))}
                  options={categories.map(c => ({ label: c.name, value: c._id }))}
                  allowClear
                  style={{ width: "100%", height: '38px' }}
                />
              </div>

              <div className="ss-filter-field">
                <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>FILTER BY RATING</span>
                <FormSelect
                  placeholder="All Ratings"
                  value={selectedRating}
                  onChange={(v) => setParams(prev => ({ ...prev, rating: v, page: 1 }))}
                  options={[
                    { label: "5 Stars", value: 5 },
                    { label: "4 Stars", value: 4 },
                    { label: "3 Stars", value: 3 },
                    { label: "2 Stars", value: 2 },
                    { label: "1 Star", value: 1 },
                  ]}
                  allowClear
                  style={{ width: "100%", height: '38px' }}
                />
              </div>

              <div className="ss-filter-field">
                <span className="ss-filter-label" style={{ color: '#94a3b8', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px' }}>CUSTOM RANGE</span>
                <DateRangePicker
                  value={dateRange}
                  onChange={(dates) => setParams(prev => ({ ...prev, dateRange: dates, page: 1 }))}
                  style={{ height: '38px', borderRadius: '10px', width: '100%' }}
                />
              </div>

              <div className="ss-filter-reset">
                <button
                  className="ss-reset-btn"
                  onClick={handleReset}
                  style={{
                    height: '38px',
                    width: '100%',
                    padding: '0 24px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <ReloadOutlined style={{ fontSize: '14px' }} />
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="pcu-border-top">
            <DataTable
              columns={columns}
              data={productFeedback}
              rowKey="id"
              loading={loading}
              onRow={(record) => ({
                style: { cursor: 'default' }
              })}
              locale={{
                emptyText: (
                  <EmptyState
                    title="No Feedback Found"
                    description={searchText ? "No feedback matches your search." : "No product feedback collected yet."}
                    gradient="purple"
                    icon="inbox"
                  />
                )
              }}
              pagination={{
                current: params.page,
                pageSize: params.limit,
                total: totalProductFeedback,
                onShowSizeChange: (c, s) => setParams(prev => ({ ...prev, limit: s, page: 1 })),
                onChange: (p) => {
                  setParams(prev => ({ ...prev, page: p }));
                  topRef.current?.scrollIntoView({ behavior: 'smooth' });
                },
              }}
            />
          </div>
        </div>

        {/* ➕ Add/Edit Modal */}
        {addProductFeedback && (
          <AddProductFeedback
            open={addProductFeedback}
            feedbackDetails={productFeedbackDetails}
            refreshFeedback={refreshProductFeedback}
            isEditing={isEditing}
          />
        )}

        {/* Feedback Detail Modal */}
        <Modal
          open={detailsVisible}
          onCancel={() => setDetailsVisible(false)}
          footer={null}
          width={650}
          closable={true}
          className="ss-modal"
          centered
          styles={{ body: { padding: 0 } }}
        >
          {modalLoading && !selectedRecord ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <Spin size="large" tip="Loading details..." />
            </div>
          ) : selectedRecord && (
            <div className="ss-modal-body" style={{ position: 'relative' }}>
              {modalLoading && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(255,255,255,0.4)', zIndex: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 16, backdropFilter: 'blur(1px)'
                }}>
                  <Spin />
                </div>
              )}
              {/* Modal Hero */}
              <div className="ss-modal-hero">
                <div className="ss-modal-img" style={{ width: 100, height: 100 }}>
                  <img
                    src={getNormalizedImageUrl(selectedRecord.product?.imageUrl)}
                    alt={selectedRecord.product?.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div className="ss-modal-info">
                  <h2 className="ss-modal-name">{selectedRecord.product?.name}</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 800, color: '#FF8800' }}>₹{selectedRecord.product?.price?.toFixed(2) || "0.00"}</span>
                    {selectedRecord.product?.mrp > selectedRecord.product?.price && (
                      <span style={{ fontSize: '13px', color: '#94a3b8', textDecoration: 'line-through' }}>₹{selectedRecord.product?.mrp?.toFixed(2)}</span>
                    )}
                  </div>
                  <div className="ss-modal-tags">
                    <span className="ss-tag ss-tag-blue" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                      <StarOutlined style={{ marginRight: 4 }} /> {selectedRecord.rating} Stars
                    </span>
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Reviewed on {new Date(selectedRecord.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* Detail Grid */}
              <div className="ss-modal-details" style={{ padding: '20px 24px' }}>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Customer Name</div>
                  <div className="ss-modal-value" style={{ fontWeight: 700 }}>
                    {selectedRecord.user ? `${selectedRecord.user.firstName} ${selectedRecord.user.lastName}` : selectedRecord.userName}
                  </div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Email</div>
                  <div className="ss-modal-value">{selectedRecord.user?.email || "N/A"}</div>
                </div>
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Phone</div>
                  <div className="ss-modal-value">{selectedRecord.user?.phone || "N/A"}</div>
                </div>
                <div className="ss-modal-row"
                  onClick={() => selectedRecord.shopId?._id && navigate(`/admin/shop/${selectedRecord.shopId._id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="ss-modal-label">Associated Shop</div>
                  <div className="ss-modal-value">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Avatar
                        src={selectedRecord.shopId?.imageUrl ? getNormalizedImageUrl(selectedRecord.shopId?.imageUrl) : null}
                        icon={<ShopOutlined style={{ color: '#64748b' }} />}
                        size={32}
                        shape="square"
                        style={{ borderRadius: '6px', border: '1px solid #e2e8f0', background: '#f8fafc' }}
                      />
                      <span style={{ fontWeight: 700, color: '#1e293b' }}>{selectedRecord.shopId?.name || "N/A"}</span>
                      <ShoppingOutlined style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }} />
                    </div>
                  </div>
                </div>
                <div className="ss-modal-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                  <div className="ss-modal-label">Customer Comment</div>
                  <div className="ss-modal-value" style={{
                    textAlign: 'left',
                    width: '100%',
                    background: '#f8fafc',
                    padding: '12px',
                    borderRadius: '8px',
                    fontStyle: selectedRecord.comment ? 'normal' : 'italic',
                    color: '#475569',
                    lineHeight: 1.5
                  }}>
                    "{selectedRecord.comment || "No comment provided."}"
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="ss-modal-footer" style={{ justifyContent: "space-between", padding: '16px 24px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => {
                      handleEditProductFeedback(selectedRecord);
                      setDetailsVisible(false);
                    }}
                    style={{ fontWeight: 700, color: '#4776E6', height: '40px', borderRadius: '10px' }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Remove this feedback?"
                    onConfirm={() => {
                      handleDeleteProductFeedback(selectedRecord);
                      setDetailsVisible(false);
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      style={{ fontWeight: 700, height: '40px', borderRadius: '10px', background: '#fff1f2', border: '1px solid #fecdd3' }}
                    >
                      Delete Feedback
                    </Button>
                  </Popconfirm>
                </div>
                <button className="ss-modal-close-btn" onClick={() => setDetailsVisible(false)} style={{ height: '40px' }}>
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default ProductFeedback;

